import { EmploymentStatus } from '@generated/graphql';
import type { CustomField } from '@interfaces/customField';
import type { PhysicalAddress } from '@interfaces/physicalAddress';
import type { AreaOfStudy, JobTitle, Profession, User } from '@interfaces/user';
import {
  getAreasOfStudy,
  getJobTitles,
  getProfessions,
} from '@interfaces/user';
import { MenuItem } from '@mui/material';
import { AddressField } from '@ovComponents/2-component/addressField/addressField';
import { Autocomplete } from '@ovComponents/2-component/autocomplete/autocomplete';
import InfoDialog from '@ovComponents/2-component/infoDialog/infoDialog';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { useCustomFieldRenderer } from '@ovComponents/3-pattern/customField/useCustomFieldRenderer';
import { additionalInfo } from '@ovComponents/4-module/configurableOptionFields/additionalInfo';
import { FieldRequiredType } from '@ovComponents/4-module/configurableOptionFields/basicField';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { Box, Grid, Typography } from 'hephaestus/web';
import { kebabCase } from 'lodash-es';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLocalization } from '../../../../../hooks/useLocalization';
import { buildEnrichedCustomOptions, invalidFields } from '../utils';

const STATUSES_REQUESTING_EMPLOYMENT_INFO = [
  'RETIRED',
  'EMPLOYED',
  'SELF_EMPLOYED',
  'UNEMPLOYED',
];

export const EmploymentInformationVisual = ({
  options,
  userData,
  loading,
  updateUser,
  continueFunc,
  activeCustomFields,
  derivedCustomFields,
  workflowCompletion,
  grid = false,
  updateMode = false,
  ignoreCustomFieldTriggerRules,
}: {
  options: any;
  userData: Partial<User>;
  loading: boolean;
  grid?: boolean;
  updateMode?: boolean;
  activeCustomFields?: string[];
  derivedCustomFields?: CustomField[];
  workflowCompletion?: any;
  updateUser: (user: Partial<User> & { customFields: any[] }) => void;
  continueFunc: () => void;
  ignoreCustomFieldTriggerRules?: boolean;
}) => {
  const { translateBackendString } = useLocalization();
  const { sendAnalytic } = useContext(AnalyticsContext);
  const { t } = useTranslation(['client', 'employmentInfo']);
  const [focused, setFocused] = useState<string[]>([]);
  const [updated, setUpdated] = useState<boolean>(false);
  const [invalidFieldsList, setInvalidFieldsList] = useState<string[]>([]);
  const [customOptionSelected, setCustomOptionSelected] = useState<any[]>([]);
  const { renderCustomFields, validateAllCustomFields } =
    useCustomFieldRenderer({
      customOptions: customOptionSelected,
      customFieldValues: userData.customFields,
    });

  useEffect(() => {
    setCustomOptionSelected(
      buildEnrichedCustomOptions(
        options,
        activeCustomFields,
        derivedCustomFields,
      ),
    );
  }, [options, activeCustomFields, derivedCustomFields]);

  const validate = () => {
    const fields = invalidFields(
      {
        ...options,
        studentAreaOfStudy: {
          ...options.studentAreaOfStudy,
          requiredIf: (data: any) => data?.employmentStatus === 'STUDENT',
        },
        companyType: {
          ...options.companyType,
          requiredIf: (data: any) =>
            ['RETIRED', 'EMPLOYED', 'SELF_EMPLOYED'].includes(
              data?.employmentStatus,
            ),
        },
        jobTitle: {
          ...options.jobTitle,
          requiredIf: (data: any) =>
            ['RETIRED', 'EMPLOYED', 'SELF_EMPLOYED'].includes(
              data?.employmentStatus,
            ),
        },
        employerCompanyAddress: {
          ...options.employerCompanyAddress,
          requiredIf: (data: any) => data?.employmentStatus === 'EMPLOYED',
        },
        employerName: {
          ...options.employerName,
          requiredIf: (data: any) =>
            ['RETIRED', 'EMPLOYED'].includes(data?.employmentStatus),
        },
        employmentSituation: {
          ...options.employmentSituation,
          requiredIf: (data: any) => data?.employmentStatus === 'EMPLOYED',
        },
      },
      userData,
    );

    const invalidCustomFieldKeys = validateAllCustomFields();
    fields.push(...invalidCustomFieldKeys);
    setInvalidFieldsList(fields);
    setFocused(fields);
    return fields.length === 0;
  };

  const submit = async () => {
    if (validate()) {
      sendAnalytic(
        ovAnalyticsEvents.workflowsEmploymentInformationContinueButtonSelect,
        {
          workflowStepTitle: options?.title,
          workflowId: workflowCompletion?.workflow?.id,
          workflowName: workflowCompletion?.workflow?.name,
          activeWorkflowCompletionId: workflowCompletion?.id,
          objectId: workflowCompletion?.objectId,
          objectType: workflowCompletion?.objectType,
        },
      );
      await continueFunc();
      setUpdated(false);
    }
  };

  const professions = getProfessions().map(
    (profession: Profession) => profession.title,
  );
  const jobTitles = getJobTitles({ professionTitle: userData.companyType }).map(
    (jobTitle: JobTitle) => jobTitle.title,
  );
  const areasOfStudy = getAreasOfStudy().map(
    (areaOfStudy: AreaOfStudy) => areaOfStudy.title,
  );

  const lockMessage = (): string =>
    options.customLockedMessage
      ? translateBackendString(options.customLockedMessage)
      : t('pageConfiguration:notEditableMessage');

  const update = (newValue: any) => {
    setUpdated(true);
    updateUser(newValue);
  };

  const isUserNotActivelyEmployed = [
    EmploymentStatus.Retired,
    EmploymentStatus.Unemployed,
  ].includes(userData.employmentStatus as EmploymentStatus);

  const companyTypeLabel = isUserNotActivelyEmployed
    ? t('employmentInfo:previousCompanyType', {
        companyType: translateBackendString(options?.companyType?.label),
      })
    : translateBackendString(options?.companyType?.label);
  const companyTypeInput = !(
    options?.companyType?.enabled &&
    userData.employmentStatus &&
    STATUSES_REQUESTING_EMPLOYMENT_INFO.includes(userData.employmentStatus)
  ) ? null : (
    <Grid size={{ xs: 12, md: grid ? 4 : 12 }}>
      {options?.companyType?.selectField?.checked ? (
        <Autocomplete
          testId='company-type'
          label={companyTypeLabel}
          infoTooltip={additionalInfo(
            translateBackendString,
            options?.companyType?.additionalInfo,
          )}
          options={professions}
          value={userData.companyType ?? ''}
          fullWidth
          onChange={(_: React.SyntheticEvent, value: string) =>
            update({ ...userData, companyType: value })
          }
          onBlur={() => setFocused([...focused, 'companyType'])}
          disabled={
            loading ||
            options?.companyType?.required === FieldRequiredType.NOT_EDITABLE
          }
          error={
            !userData.companyType &&
            focused.includes('companyType') &&
            options?.companyType?.required !== FieldRequiredType.NOT_REQUIRED
          }
          locked={
            options?.companyType?.required === FieldRequiredType.NOT_EDITABLE
          }
          lockMessage={lockMessage()}
          renderOption={(props: any, option: any) => (
            <li
              {...props}
              data-testid={`company-type-autocomplete-${kebabCase(option)}`}
            >
              {option}
            </li>
          )}
        />
      ) : (
        <TextField
          testId='company-type'
          onChange={(e: any) =>
            update({ ...userData, companyType: e.target.value })
          }
          disabled={
            loading ||
            options?.companyType?.required === FieldRequiredType.NOT_EDITABLE
          }
          locked={
            options?.companyType?.required === FieldRequiredType.NOT_EDITABLE
          }
          lockMessage={lockMessage()}
          label={companyTypeLabel}
          infoTooltip={additionalInfo(
            translateBackendString,
            options?.companyType?.additionalInfo,
          )}
          fullWidth
          value={userData.companyType ?? ''}
          onBlur={() => setFocused([...focused, 'companyType'])}
          error={
            ((!userData.companyType && focused.includes('companyType')) ||
              invalidFieldsList.includes('companyType')) &&
            options?.companyType?.required !== FieldRequiredType.NOT_REQUIRED
          }
        />
      )}
    </Grid>
  );

  const jobTitleLabel = isUserNotActivelyEmployed
    ? t('employmentInfo:previousJobTitle', {
        jobTitle: translateBackendString(options?.jobTitle?.label),
      })
    : translateBackendString(options?.jobTitle?.label);
  const jobTitleInput = !(
    options?.jobTitle?.enabled &&
    userData.employmentStatus &&
    STATUSES_REQUESTING_EMPLOYMENT_INFO.includes(userData.employmentStatus)
  ) ? null : (
    <Grid size={{ xs: 12, md: grid ? 4 : 12 }}>
      {options?.jobTitle?.selectField?.checked ? (
        <Autocomplete
          testId='job-title'
          label={jobTitleLabel}
          infoTooltip={additionalInfo(
            translateBackendString,
            options?.jobTitle?.additionalInfo,
          )}
          options={jobTitles}
          value={userData.jobTitle ?? ''}
          fullWidth
          onChange={(_: React.SyntheticEvent, value: string) =>
            update({ ...userData, jobTitle: value })
          }
          onBlur={() => setFocused([...focused, 'jobTitle'])}
          disabled={
            loading ||
            options?.jobTitle?.required === FieldRequiredType.NOT_EDITABLE
          }
          error={
            !userData.jobTitle &&
            focused.includes('jobTitle') &&
            options?.jobTitle?.required !== FieldRequiredType.NOT_REQUIRED
          }
          locked={
            options?.jobTitle?.required === FieldRequiredType.NOT_EDITABLE
          }
          lockMessage={lockMessage()}
          renderOption={(props: any, option: any) => (
            <li {...props} data-testid={`job-title-${kebabCase(option)}`}>
              {option}
            </li>
          )}
        />
      ) : (
        <TextField
          testId='job-title'
          onChange={(e: any) =>
            update({ ...userData, jobTitle: e.target.value })
          }
          disabled={
            loading ||
            options?.jobTitle?.required === FieldRequiredType.NOT_EDITABLE
          }
          locked={
            options?.jobTitle?.required === FieldRequiredType.NOT_EDITABLE
          }
          lockMessage={lockMessage()}
          label={jobTitleLabel}
          infoTooltip={additionalInfo(
            translateBackendString,
            options?.jobTitle?.additionalInfo,
          )}
          fullWidth
          value={userData.jobTitle ?? ''}
          onBlur={() => setFocused([...focused, 'jobTitle'])}
          error={
            ((!userData.jobTitle && focused.includes('jobTitle')) ||
              invalidFieldsList.includes('jobTitle')) &&
            options?.jobTitle?.required !== FieldRequiredType.NOT_REQUIRED
          }
        />
      )}
    </Grid>
  );

  const areaOfStudyInput = !(
    options?.studentAreaOfStudy?.enabled &&
    userData.employmentStatus === 'STUDENT'
  ) ? null : (
    <Grid size={{ xs: 12, md: grid ? 4 : 12 }}>
      {options?.studentAreaOfStudy?.selectField?.checked ? (
        <Autocomplete
          testId='student-area-of-study'
          label={translateBackendString(options?.studentAreaOfStudy?.label)}
          infoTooltip={additionalInfo(
            translateBackendString,
            options?.studentAreaOfStudy?.additionalInfo,
          )}
          options={areasOfStudy}
          value={userData.studentAreaOfStudy ?? ''}
          fullWidth
          onChange={(_: React.SyntheticEvent, value: string) =>
            update({ ...userData, studentAreaOfStudy: value })
          }
          onBlur={() => setFocused([...focused, 'studentAreaOfStudy'])}
          disabled={
            loading ||
            options?.studentAreaOfStudy?.required ===
              FieldRequiredType.NOT_EDITABLE
          }
          error={
            !userData.studentAreaOfStudy &&
            focused.includes('studentAreaOfStudy') &&
            options?.studentAreaOfStudy?.required !==
              FieldRequiredType.NOT_REQUIRED
          }
          locked={
            options?.studentAreaOfStudy?.required ===
            FieldRequiredType.NOT_EDITABLE
          }
          lockMessage={lockMessage()}
          renderOption={(props: any, option: any) => (
            <li
              {...props}
              data-testid={`student-area-of-study-${kebabCase(option)}`}
            >
              {option}
            </li>
          )}
        />
      ) : (
        <TextField
          testId='student-area-of-study'
          onChange={(e: any) =>
            update({ ...userData, studentAreaOfStudy: e.target.value })
          }
          disabled={
            loading ||
            options?.studentAreaOfStudy?.required ===
              FieldRequiredType.NOT_EDITABLE
          }
          locked={
            options?.studentAreaOfStudy?.required ===
            FieldRequiredType.NOT_EDITABLE
          }
          lockMessage={lockMessage()}
          label={translateBackendString(options?.studentAreaOfStudy?.label)}
          infoTooltip={additionalInfo(
            translateBackendString,
            options?.studentAreaOfStudy?.additionalInfo,
          )}
          fullWidth
          value={userData.studentAreaOfStudy}
          onBlur={() => setFocused([...focused, 'studentAreaOfStudy'])}
          error={
            ((!userData.studentAreaOfStudy &&
              focused.includes('studentAreaOfStudy')) ||
              invalidFieldsList.includes('studentAreaOfStudy')) &&
            options?.studentAreaOfStudy?.required !==
              FieldRequiredType.NOT_REQUIRED
          }
        />
      )}
    </Grid>
  );

  return (
    <WorkflowCompletionForm
      onSubmit={submit}
      testId='employment-information-form'
      showSubmitButton={!updateMode || options.displayUpdateButton}
      submitButtonProps={{
        label: t(updateMode ? 'update' : 'continue'),
        disabled: loading || (!updated && updateMode),
        dataTestId: 'employment-information-continue-button',
      }}
    >
      {options?.title && (
        <Typography variant='displayLarge' sx={{ mt: 1, mb: 3 }}>
          {translateBackendString(options?.title)}
          <Box
            sx={{
              display: 'inline-flex',
            }}
          >
            {additionalInfo(
              translateBackendString,
              options?.title?.additionalInfo,
            ) && (
              <InfoDialog
                information={
                  additionalInfo(
                    translateBackendString,
                    options?.title?.additionalInfo,
                  ) ?? ''
                }
              />
            )}
          </Box>
        </Typography>
      )}
      {options?.subtitle && (
        <Typography
          variant='bodyLarge'
          component={'div'}
          sx={{
            mb: 3,
            table: { width: '100%' },
            display: 'flex',
            alignItems: 'end',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexDirection: 'column',
              m: 0,
              '& > *': { m: 0 },
            }}
          >
            <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
              {translateBackendString(options?.subtitle)}
            </ReactMarkdown>
          </Box>
          {additionalInfo(
            translateBackendString,
            options?.subtitle?.additionalInfo,
          ) && (
            <InfoDialog
              information={
                additionalInfo(
                  translateBackendString,
                  options?.subtitle?.additionalInfo,
                ) ?? ''
              }
            />
          )}
        </Typography>
      )}

      <Grid container spacing={2}>
        {options?.employmentStatus?.enabled && (
          <Grid size={{ xs: 12, md: grid ? 4 : 12 }}>
            <SelectField
              testId='employment-status'
              onChange={(e: any) =>
                update({ ...userData, employmentStatus: e.target.value })
              }
              disabled={
                loading ||
                options?.employmentStatus?.required ===
                  FieldRequiredType.NOT_EDITABLE
              }
              locked={
                options?.employmentStatus?.required ===
                FieldRequiredType.NOT_EDITABLE
              }
              lockMessage={lockMessage()}
              label={translateBackendString(options?.employmentStatus?.label)}
              infoTooltip={additionalInfo(
                translateBackendString,
                options?.employmentStatus?.additionalInfo,
              )}
              fullWidth
              value={userData.employmentStatus ?? ''}
              onBlur={() => setFocused([...focused, 'employmentStatus'])}
              error={
                ((!userData.employmentStatus &&
                  focused.includes('employmentStatus')) ||
                  invalidFieldsList.includes('employmentStatus')) &&
                options?.employmentStatus?.required !==
                  FieldRequiredType.NOT_REQUIRED
              }
            >
              <MenuItem
                data-testid='employment-status-employed'
                value='EMPLOYED'
              >
                {t('edit.employmentStatusOptions.EMPLOYED')}
              </MenuItem>
              <MenuItem
                data-testid='employment-status-self-employed'
                value='SELF_EMPLOYED'
              >
                {t('edit.employmentStatusOptions.SELF_EMPLOYED')}
              </MenuItem>
              <MenuItem
                data-testid='employment-status-unemployed'
                value='UNEMPLOYED'
              >
                {t('edit.employmentStatusOptions.UNEMPLOYED')}
              </MenuItem>
              <MenuItem
                data-testid='employment-status-stay-at-home-parent'
                value='STAY_AT_HOME_PARENT'
              >
                {t('edit.employmentStatusOptions.STAY_AT_HOME_PARENT')}
              </MenuItem>
              <MenuItem data-testid='employment-status-student' value='STUDENT'>
                {t('edit.employmentStatusOptions.STUDENT')}
              </MenuItem>
              <MenuItem data-testid='employment-status-retired' value='RETIRED'>
                {t('edit.employmentStatusOptions.RETIRED')}
              </MenuItem>
            </SelectField>
          </Grid>
        )}

        {companyTypeInput}
        {jobTitleInput}
        {areaOfStudyInput}

        {options?.employerName?.enabled &&
          (userData.employmentStatus === 'EMPLOYED' ||
            isUserNotActivelyEmployed) && (
            <Grid size={{ xs: 12, md: grid ? 4 : 12 }}>
              <TextField
                testId='employer-name'
                onChange={(e: any) =>
                  update({ ...userData, employerName: e.target.value })
                }
                disabled={
                  loading ||
                  options?.employerName?.required ===
                    FieldRequiredType.NOT_EDITABLE
                }
                locked={
                  options?.employerName?.required ===
                  FieldRequiredType.NOT_EDITABLE
                }
                lockMessage={lockMessage()}
                label={
                  isUserNotActivelyEmployed
                    ? t('employmentInfo:previousEmployerName', {
                        employerName: translateBackendString(
                          options?.employerName?.label,
                        ),
                      })
                    : translateBackendString(options?.employerName?.label)
                }
                infoTooltip={additionalInfo(
                  translateBackendString,
                  options?.employerName?.additionalInfo,
                )}
                fullWidth
                value={userData.employerName ?? ''}
                onBlur={() => setFocused([...focused, 'employerName'])}
                error={
                  ((!userData.employerName &&
                    focused.includes('employerName')) ||
                    invalidFieldsList.includes('employerName')) &&
                  options?.employerName?.required !==
                    FieldRequiredType.NOT_REQUIRED
                }
              />
            </Grid>
          )}
        {options?.employerCompanyAddress?.enabled &&
          userData.employmentStatus === 'EMPLOYED' && (
            <Grid size={{ xs: 12, md: grid ? 4 : 12 }}>
              <AddressField
                testId='employer-company-address'
                onChange={(e: PhysicalAddress) =>
                  update({ ...userData, employerCompanyAddress: e })
                }
                disabled={
                  loading ||
                  options?.employerCompanyAddress?.required ===
                    FieldRequiredType.NOT_EDITABLE
                }
                label={translateBackendString(
                  options?.employerCompanyAddress?.label,
                )}
                infoTooltip={additionalInfo(
                  translateBackendString,
                  options?.employerCompanyAddress?.additionalInfo,
                )}
                lockMessage={lockMessage()}
                fullWidth
                address={userData.employerCompanyAddress as PhysicalAddress}
                onFocus={() =>
                  setFocused([...focused, 'employerCompanyAddress'])
                }
                error={
                  ((!userData.employerCompanyAddress?.streetName &&
                    focused.includes('employerCompanyAddress')) ||
                    invalidFieldsList.includes('employerCompanyAddress')) &&
                  focused.includes('employerCompanyAddress') &&
                  options?.employerCompanyAddress?.required !==
                    FieldRequiredType.NOT_REQUIRED
                }
              />
            </Grid>
          )}

        {/* TODO employerCompanyPhone */}

        {options?.employmentSituation?.enabled &&
          userData.employmentStatus === 'EMPLOYED' && (
            <Grid size={{ xs: 12, md: grid ? 4 : 12 }}>
              <SelectField
                testId='employment-situation'
                onChange={(e: any) =>
                  update({ ...userData, employmentSituation: e.target.value })
                }
                disabled={
                  loading ||
                  options?.employmentSituation?.required ===
                    FieldRequiredType.NOT_EDITABLE
                }
                locked={
                  options?.employmentSituation?.required ===
                  FieldRequiredType.NOT_EDITABLE
                }
                lockMessage={lockMessage()}
                label={translateBackendString(
                  options?.employmentSituation?.label,
                )}
                infoTooltip={additionalInfo(
                  translateBackendString,
                  options?.employmentSituation?.additionalInfo,
                )}
                fullWidth
                value={userData.employmentSituation ?? ''}
                onBlur={() => setFocused([...focused, 'employmentSituation'])}
                error={
                  ((!userData.employmentSituation &&
                    focused.includes('employmentSituation')) ||
                    invalidFieldsList.includes('employmentSituation')) &&
                  options?.employmentSituation?.required !==
                    FieldRequiredType.NOT_REQUIRED
                }
              >
                <MenuItem
                  data-testid='employment-situation-stable-and-secure'
                  value='STABLE_AND_SECURE'
                >
                  {t('edit.employmentSituationOptions.STABLE_AND_SECURE')}
                </MenuItem>
                <MenuItem
                  data-testid='employment-situation-may-change-in-future'
                  value='STABLE_BUT_MAY_CHANGE_IN_FUTURE'
                >
                  {t(
                    'edit.employmentSituationOptions.STABLE_BUT_MAY_CHANGE_IN_FUTURE',
                  )}
                </MenuItem>
                <MenuItem
                  data-testid='employment-situation-somewhat-stabe'
                  value='SOMEWHAT_STABLE'
                >
                  {t('edit.employmentSituationOptions.SOMEWHAT_STABLE')}
                </MenuItem>
                <MenuItem
                  data-testid='employment-situation-unemployed-but-looking'
                  value='UNEMPLOYED_BUT_LOOKING'
                >
                  {t('edit.employmentSituationOptions.UNEMPLOYED_BUT_LOOKING')}
                </MenuItem>
                <MenuItem
                  data-testid='employment-situation-other'
                  value='OTHER'
                >
                  {t('edit.employmentSituationOptions.OTHER')}
                </MenuItem>
              </SelectField>
            </Grid>
          )}
        {options?.employmentSituation?.enabled &&
          userData.employmentSituation === 'OTHER' && (
            <Grid size={{ xs: 12, md: grid ? 4 : 12 }}>
              <TextField
                testId='employment-situation-other-description'
                onChange={(e: any) =>
                  update({
                    ...userData,
                    employmentSituationOtherDescription: e.target.value,
                  })
                }
                disabled={
                  loading ||
                  options?.employmentSituation?.required ===
                    FieldRequiredType.NOT_EDITABLE
                }
                locked={
                  options?.employmentSituation?.required ===
                  FieldRequiredType.NOT_EDITABLE
                }
                lockMessage={lockMessage()}
                label={''}
                placeholder={t('other')}
                fullWidth
                value={userData.employmentSituationOtherDescription ?? ''}
                onBlur={() => setFocused([...focused, 'employmentSituation'])}
                error={
                  ((!userData.employmentSituationOtherDescription &&
                    focused.includes('employmentSituation')) ||
                    invalidFieldsList.includes('employmentSituation')) &&
                  focused.includes('employmentSituation') &&
                  options?.employmentSituation?.required !==
                    FieldRequiredType.NOT_REQUIRED
                }
              />
            </Grid>
          )}

        <Grid size={12}>
          {renderCustomFields({
            customData: userData,
            update,
            focused,
            setFocused,
            loading,
            grid,
            ignoreCustomFieldTriggerRules,
          })}
        </Grid>
      </Grid>
    </WorkflowCompletionForm>
  );
};

export default EmploymentInformationVisual;
